import request from '~/utils/request';
import {
  SET_ADMIN_PRICING_POSTAGE_DATA,
  SET_ADMIN_PRICING_POSTAGE_LOADER,
  SET_ADMIN_PRICING_POSTAGE_HISTORICAL_DATA,
  SET_ADMIN_PRICING_PLANS_DATA,
  SET_ADMIN_PRICING_PLANS_LOADER,
  SET_ADMIN_PRICING_COST_OF_GOODS_DATA,
  SET_ADMIN_PRICING_COST_OF_GOODS_HISTORICAL_DATA,
  SET_ADMIN_PRICING_COST_OF_GOODS_LOADER,
  SET_ADMIN_PRICING_TIERS_DATA,
  SET_ADMIN_PRICING_TIERS_LOADER,
} from './action-types';

// Postage Actions
export const fetchAdminPostageData =
  (payload = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: SET_ADMIN_PRICING_POSTAGE_LOADER, payload: true });

      // Build query parameters following the same pattern as other admin actions
      let queryPayload = {
        page: payload?.pagination?.page || 1,
        page_size: payload?.pagination?.pageSize || 20,
      };

      const response = await request.get('admin/pricing-controls/postage', queryPayload);
      if (response.status === 200) {
        dispatch({
          type: SET_ADMIN_PRICING_POSTAGE_DATA,
          payload: response.data,
        });
      }
      return response;
    } catch (error) {
      return error.response;
    } finally {
      dispatch({ type: SET_ADMIN_PRICING_POSTAGE_LOADER, payload: false });
    }
  };

export const fetchAdminPostageById = (id) => async (dispatch) => {
  try {
    const response = await request.get(`admin/pricing-controls/postage/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

export const fetchAdminPostageHistoricalData =
  (payload = {}) =>
  async (dispatch) => {
    try {
      // Build query parameters following the same pattern as other admin actions
      let queryPayload = {
        page: payload?.pagination?.page || 1,
        page_size: payload?.pagination?.pageSize || 20,
        is_archived: true, // Always include archived filter for historical data
      };

      const response = await request.get('admin/pricing-controls/postage', queryPayload);
      if (response.status === 200) {
        dispatch({
          type: SET_ADMIN_PRICING_POSTAGE_HISTORICAL_DATA,
          payload: response.data,
        });
      }
      return response;
    } catch (error) {
      return error.response;
    }
  };

export const createAdminPostage = (payload) => async (dispatch) => {
  try {
    const response = await request.post('admin/pricing-controls/postage', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

export const updateAdminPostage = (payload) => async (dispatch) => {
  try {
    const response = await request.put(`admin/pricing-controls/postage/${payload.id}`, payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

export const deleteAdminPostage = (id) => async (dispatch) => {
  try {
    const response = await request.delete(`admin/pricing-controls/postage/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

// Plans Actions
export const fetchAdminPlansData =
  (payload = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: SET_ADMIN_PRICING_PLANS_LOADER, payload: true });
      const response = await request.get('admin/pricing-controls/plans', payload);
      if (response.status === 200) {
        dispatch({
          type: SET_ADMIN_PRICING_PLANS_DATA,
          payload: response.data.data,
        });
      }
      return response;
    } catch (error) {
      return error.response;
    } finally {
      dispatch({ type: SET_ADMIN_PRICING_PLANS_LOADER, payload: false });
    }
  };

// Fetch Plans Data by Version ID
export const fetchAdminPlansById = (versionId) => async (dispatch) => {
  try {
    dispatch({ type: SET_ADMIN_PRICING_PLANS_LOADER, payload: true });
    const response = await request.get(`admin/pricing-controls/plans/${versionId}`);
    if (response.status === 200) {
      // API returns array directly or wrapped in data property
      const plansData = response.data.data.versions || response.data.versions;
      const plansArray = Array.isArray(plansData) ? plansData : [plansData];
      const meta = response.data.data.meta || response.data.meta;
      
      dispatch({
        type: SET_ADMIN_PRICING_PLANS_DATA,
        payload: {
          plans: plansArray,
          rows: plansArray,
          meta
        },
      });
    }
    return response;
  } catch (error) {
    return error.response;
  } finally {
    dispatch({ type: SET_ADMIN_PRICING_PLANS_LOADER, payload: false });
  }
};

export const updateAdminPlanFeatures = (payload) => async (dispatch) => {
  try {
    const response = await request.post('admin/pricing-controls/plans/update', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

export const fetchAdminPlansOptions = () => async (dispatch) => {
  try {
    const response = await request.get('admin/pricing-controls/plans/options/get');
    return response;
  } catch (error) {
    return error.response;
  }
};

export const assignPlanToAccount = (payload) => async (dispatch) => {
  try {
    const response = await request.post('admin/pricing-controls/plans/assign', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

export const updatePlanTierAccess = (payload) => async (dispatch) => {
  try {
    const response = await request.put('admin/pricing-controls/plans/tier-access', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

export const deletePlanVersion = (id) => async (dispatch) => {
  try {
    const response = await request.delete(`admin/pricing-controls/plans/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

// Cost of Goods Actions
export const fetchAdminCostOfGoodsData =
  (payload = {}) =>
  async (dispatch) => {
    try {
      let queryPayload = {
        page: payload?.pagination?.page || 1,
        page_size: payload?.pagination?.pageSize || 15,
        product_type: payload.product_type,
      };
      dispatch({ type: SET_ADMIN_PRICING_COST_OF_GOODS_LOADER, payload: true });
      const response = await request.get('admin/pricing-controls/cost-of-goods', queryPayload);
      if (response.status === 200) {
        dispatch({
          type: SET_ADMIN_PRICING_COST_OF_GOODS_DATA,
          payload: response.data,
        });
      }
      return response;
    } catch (error) {
      return error.response;
    } finally {
      dispatch({ type: SET_ADMIN_PRICING_COST_OF_GOODS_LOADER, payload: false });
    }
  };

export const fetchAdminCostOfGoodsById = (id) => async (dispatch) => {
  try {
    const response = await request.get(`admin/pricing-controls/cost-of-goods/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

export const fetchAdminCostOfGoodsHistoricalData =
  (payload = {}) =>
  async (dispatch) => {
    let queryPayload = {
      page: payload?.pagination?.page || 1,
      page_size: payload?.pagination?.pageSize || 15,
      product_type: payload.product_type,
      is_archived: payload.is_archived,
    };
    try {
      const response = await request.get(
        'admin/pricing-controls/cost-of-goods?is_archived=true',
        queryPayload
      );
      if (response.status === 200) {
        dispatch({
          type: SET_ADMIN_PRICING_COST_OF_GOODS_HISTORICAL_DATA,
          payload: response.data,
        });
      }
      return response;
    } catch (error) {
      return error.response;
    }
  };

export const createAdminCostOfGoods = (payload) => async (dispatch) => {
  try {
    const response = await request.post('admin/pricing-controls/cost-of-goods', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

export const updateAdminCostOfGoods = (payload) => async (dispatch) => {
  try {
    const response = await request.put(
      `admin/pricing-controls/cost-of-goods/${payload.id}`,
      payload
    );
    return response;
  } catch (error) {
    return error.response;
  }
};

export const deleteAdminCostOfGoods = (id) => async (dispatch) => {
  try {
    const response = await request.delete(`admin/pricing-controls/cost-of-goods/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

// Pricing Tiers Actions
export const fetchAdminPricingTiersData =
  (payload = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: SET_ADMIN_PRICING_TIERS_LOADER, payload: true });
      const response = await request.get('admin/pricing-controls/pricing', payload);
      if (response.status === 200) {
        dispatch({
          type: SET_ADMIN_PRICING_TIERS_DATA,
          payload: response.data.data,
        });
      }
      return response;
    } catch (error) {
      return error.response;
    } finally {
      dispatch({ type: SET_ADMIN_PRICING_TIERS_LOADER, payload: false });
    }
  };


// General pricing data fetch (for main component)
export const fetchAdminPricingOverview = () => async (dispatch) => {
  try {
    const response = await request.get('admin/pricing-controls/overview');
    return response;
  } catch (error) {
    return error.response;
  }
};

// Pricing Tiers Filters Data
export const fetchAdminPricingTiersFiltersData = () => async (dispatch) => {
  try {
    const response = await request.get('admin/pricing-controls/tiers/filters-data');
    if (response.status === 200) {
      return response;
    }
    return response;
  } catch (error) {
    return error.response;
  }
};

// Plans Filters Data
export const fetchAdminPlansFiltersData = () => async (dispatch) => {
  try {
    const response = await request.get('admin/pricing-controls/plans/filters/get');
    if (response.status === 200) {
      return response;
    }
    return response;
  } catch (error) {
    return error.response;
  }
};

// Plans Versions Data
export const fetchAdminPlansVersionsData =
  (payload = {}) =>
  async (dispatch) => {
    try {
      const response = await request.get('admin/pricing-controls/plans/versions', payload);
      if (response.status === 200) {
        return response;
      }
      return response;
    } catch (error) {
      return error.response;
    }
  };

// Pricing Tiers Versions Data
export const fetchAdminPricingTiersVersionsData =
  (payload = {}) =>
  async (dispatch) => {
    try {
      let queryPayload = {};

      if (payload.product) {
        queryPayload.product = payload.product;
      }

      if (payload.account_id) {
        queryPayload.account_id = payload.account_id;
      }
      
      const response = await request.get('admin/pricing-controls/tiers/versions', queryPayload);
      if (response.status === 200) {
        return response;
      }
      return response;
    } catch (error) {
      return error.response;
    }
  };

// Get Tiered Pricing Data
export const fetchAdminTieredPricingData = (versionId) => async (dispatch) => {
  try {
    dispatch({ type: SET_ADMIN_PRICING_TIERS_LOADER, payload: true });
    const response = await request.get(`admin/pricing-controls/tiers/${versionId}`);
    if (response.status === 200) {
      dispatch({
        type: SET_ADMIN_PRICING_TIERS_DATA,
        payload: response.data,
      });
      return response;
    }
    return response;
  } catch (error) {
    return error.response;
  } finally {
    dispatch({ type: SET_ADMIN_PRICING_TIERS_LOADER, payload: false });
  }
};

// Delete Pricing Tier
export const deleteAdminPricingTier = (tierId) => async (dispatch) => {
  try {
    const response = await request.delete(`admin/pricing-controls/tiers/${tierId}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

// Update Pricing Tiers
export const updateAdminPricingTiers = (payload, id) => async (dispatch) => {
  try {
    const response = await request.put(`admin/pricing-controls/tiers/${id}`, payload);
    return response;
  } catch (error) {
    return error.response;
  }
};
